import React from "react";
import { Control, FieldErrors, FieldName } from "react-hook-form";
import { ControlledInput } from "../../ControlledForm";
import { ControlledPhoneNumber } from "./ControlledPhoneNumber";
import ControlledAutofilledAddress from "./ControlledAutofilledAddress";
import { useBreakpoint } from "../../../features/global/hooks/useBreakpoint";
import { ContactFieldType } from "../../../queries/crmTopics";

interface ControlledContactDetailsProps<TFormData extends Record<string, any>> {
  control: Control<TFormData>;
  errors: FieldErrors<TFormData>;
  firstNameFieldName?: FieldName<TFormData>;
  lastNameFieldName?: FieldName<TFormData>;
  addressFieldName?: FieldName<TFormData>;
  emailFieldName?: FieldName<TFormData>;
  phoneFieldName?: FieldName<TFormData>;
  altPhoneFieldName?: FieldName<TFormData>;
  wrap?: boolean; // Should wrap fields to single lines on small screens
  requiredFields?: ContactFieldType[];
}

export const ControlledContactDetails = <TFormData extends Record<string, any>>({
  control,
  errors,
  firstNameFieldName = "firstName" as FieldName<TFormData>,
  lastNameFieldName = "lastName" as FieldName<TFormData>,
  emailFieldName = "email" as FieldName<TFormData>,
  phoneFieldName = "phone" as FieldName<TFormData>,
  altPhoneFieldName = "altPhone" as FieldName<TFormData>,
  addressFieldName = "address" as FieldName<TFormData>,
  wrap = false,
  requiredFields = [],
}: ControlledContactDetailsProps<TFormData>) => {
  // ControlledAutofilledAddress renders as a grid, need to simulate flex spacing
  const { sm } = useBreakpoint();
  const customAddressLayout =
    sm && wrap
      ? [
          [{ field: "address", width: 16, class: "tw-px-0" }],
          [{ field: "address2", width: 16, class: "tw-px-0" }],
          [{ field: "city", width: 16, class: "tw-px-0" }],
          [
            { field: "state", width: 8, class: "tw-pl-0 tw-pr-2" },
            { field: "zip", width: 8, class: "tw-pl-2 tw-pr-0" },
          ],
        ]
      : [
          [
            { field: "address", width: 8, class: "tw-pl-0 tw-pr-1 xs:tw-pr-2" }, // 50% (8/16)
            { field: "address2", width: 8, class: "tw-pl-1 xs:tw-pl-2 tw-pr-0" }, // 50% (8/16)
          ],
          [
            { field: "city", width: 8, class: "tw-pl-0 tw-pr-1 xs:tw-pr-2" }, // 50% (8/16)
            { field: "state", width: 4, class: "tw-pl-1 xs:tw-pl-2 tw-pr-1 xs:tw-pr-2" }, // 25% (4/16)
            { field: "zip", width: 4, class: "tw-pl-1 xs:tw-pl-2 tw-pr-0" }, // 25% (4/16)
          ],
        ];

  return (
    <div className='tw-space-y-6'>
      {/* First and Last Name */}
      <div className='tw-flex tw-gap-2 xs:tw-gap-4'>
        <div className='tw-w-1/2'>
          <FieldLabel htmlFor={firstNameFieldName} label='First Name' required={requiredFields.includes("FirstName")} />
          <ControlledInput
            name={firstNameFieldName}
            control={control}
            placeholder='Enter first name'
            errors={errors}
            className='tw-w-full'
            rules={requiredFields.includes("FirstName") ? { required: "First name is required" } : {}}
          />
        </div>
        <div className='tw-w-1/2'>
          <FieldLabel htmlFor={lastNameFieldName} label='Last Name' required={requiredFields.includes("LastName")} />
          <ControlledInput
            name={lastNameFieldName}
            control={control}
            placeholder='Enter last name'
            errors={errors}
            className='tw-w-full'
            rules={requiredFields.includes("LastName") ? { required: "Last name is required" } : {}}
          />
        </div>
      </div>

      {/* Email */}
      <div>
        <FieldLabel htmlFor={emailFieldName} label='Email' required={requiredFields.includes("Email")} />
        <ControlledInput
          name={emailFieldName}
          control={control}
          type='email'
          placeholder='Enter email address'
          errors={errors}
          rules={{
            required: requiredFields.includes("Email") ? "Email is required" : false,
            pattern: {
              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
              message: "Invalid email address",
            },
          }}
          fluid
        />
      </div>

      {/* Phone */}
      <div className='tw-flex tw-flex-col tw-gap-6 xs:tw-gap-4 sm:tw-flex-row sm:tw-gap-2'>
        <div className='tw-w-full sm:tw-w-1/2'>
          <FieldLabel htmlFor={phoneFieldName} label='Phone' required={requiredFields.includes("Phone")} />
          <ControlledPhoneNumber
            name={phoneFieldName}
            control={control as any}
            className='tw-w-full'
            placeholder='Enter phone number'
            required={requiredFields.includes("Phone")}
            errors={errors}
          />
        </div>
        <div className='tw-w-full sm:tw-w-1/2'>
          <FieldLabel htmlFor={altPhoneFieldName} label='Alt Phone' />
          <ControlledPhoneNumber
            name={altPhoneFieldName}
            control={control as any}
            className='tw-w-full'
            placeholder='Enter alternate phone'
            errors={errors}
          />
        </div>
      </div>

      {/* Mailing Address */}
      <div>
        <ControlledAutofilledAddress
          name={addressFieldName}
          control={control}
          errors={errors}
          required={requiredFields.includes("Address")}
          showCountryField={false}
          autofillCountry
          customLayout={customAddressLayout}
          customGridClass='tw-w-full tw-m-0 -tw-mt-4'
          labelClassName='tw-font-bold tw-text-[13px] tw-mb-[4px] tw-leading-[24px] tw-tracking-[0.15px]'
        />
      </div>
    </div>
  );
};

const FieldLabel = ({
  htmlFor,
  label,
  required,
  className,
}: {
  htmlFor: string;
  label: string;
  required?: boolean;
  className?: string;
}) => {
  const fieldLabelClass = "tw-font-bold tw-text-[13px] tw-mb-[4px] tw-leading-[24px] tw-tracking-[0.15px] tw-block";
  return (
    <label htmlFor={htmlFor} className={`${fieldLabelClass} ${className || ""}`}>
      {label}
      {required && <span className='tw-ml-1 tw-text-red'>*</span>}
    </label>
  );
};
